import { useEffect, useRef, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { PageHeader, TableShell } from "@/components/EntityManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Pencil, Trash2, Upload, ExternalLink, IdCard, FileCheck2, Printer, AlertTriangle, MessageCircle, History, Minimize2, Maximize2 } from "lucide-react";
import { fmtDate, today } from "@/lib/format";
import { printDriverMaster, printDriverTrips } from "@/lib/print";
import { BRANCHES } from "@/lib/branches";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const dlExpired = (r) => r?.dl_expiry && String(r.dl_expiry).slice(0, 10) < today();

const dlDue = (r) => {
  const d = String(r?.dl_expiry || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return false;
  return d < today() || d.slice(0, 7) === today().slice(0, 7);
};

const dlChaseWa = (r) => {
  const d = String(r.dl_expiry || "").slice(0, 10);
  const exp = d < today();
  const msg = [
    "⚠ *NTC — Driving Licence Renewal*",
    `Dear ${r.driver_name || "Driver"},`,
    "",
    `Your driving licence ${r.license_no || ""} ${exp ? `EXPIRED on ${fmtDate(d)}` : `expires on ${fmtDate(d)} (this month)`}.`,
    "Please renew it immediately and share the updated copy, else you cannot be engaged on our vehicles.",
    "",
    "— Nisha Transport Corporation",
  ].join("\n");
  const ph = String(r.mobile || "").replace(/\D/g, "");
  const num = ph ? `91${ph.slice(-10)}` : "";
  window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
};

const dlChaseWaAll = (list) => {
  const msg = [
    "⚠ *NTC — Driver Licence Renewals Due*",
    `${list.length} driver(s) with DL expired or expiring this month:`,
    "",
    ...list.map((r) => `• ${r.driver_name || "—"} (${r.mobile || "no mobile"}) — ${r.license_no || "DL"} ${String(r.dl_expiry).slice(0, 10) < today() ? "EXPIRED" : "expires"} ${fmtDate(String(r.dl_expiry).slice(0, 10))}${r.vehicle_no ? ` — ${r.vehicle_no}` : ""}`),
    "",
    "Chase the drivers for renewals.",
  ].join("\n");
  const digits = String(localStorage.getItem("ntc_company_mobile") || "").replace(/\D/g, "");
  const num = digits ? (digits.length === 10 ? "91" + digits : digits) : "";
  window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
};

const DOC_EXP = [["RC", "rc_expiry"], ["Insurance", "insurance_expiry"], ["PUC", "puc_expiry"], ["Permit", "permit_expiry"]];

const docsDue = (r) => DOC_EXP.map(([label, k]) => {
  const d = String(r?.[k] || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
  return d < today() || d.slice(0, 7) === today().slice(0, 7) ? { label, date: d, expired: d < today() } : null;
}).filter(Boolean);

const docHealth = (r) => {
  const dates = [["DL", r?.dl_expiry], ...DOC_EXP.map(([l, k]) => [l, r?.[k]])]
    .map(([l, d]) => [l, String(d || "").slice(0, 10)])
    .filter(([, d]) => /^\d{4}-\d{2}-\d{2}$/.test(d));
  if (!dates.length) return { level: "none", items: [] };
  const expired = dates.filter(([, d]) => d < today());
  if (expired.length) return { level: "red", items: expired };
  const soon = dates.filter(([, d]) => d.slice(0, 7) === today().slice(0, 7));
  if (soon.length) return { level: "amber", items: soon };
  return { level: "green", items: [] };
};

const docChaseWa = (r) => {
  const due = docsDue(r);
  const msg = [
    "⚠ *NTC — Vehicle Document Renewal*",
    `Dear ${r.owner_name || "Vehicle Owner"},`,
    "",
    `Your vehicle ${r.vehicle_no || ""} has document(s) lapsing:`,
    ...due.map((d) => `• ${d.label}: ${d.expired ? `EXPIRED on ${fmtDate(d.date)}` : `expires ${fmtDate(d.date)} (this month)`}`),
    "",
    "Please renew immediately and share the updated copies, else the vehicle cannot be engaged on our routes.",
    "",
    "— Nisha Transport Corporation",
  ].join("\n");
  const ph = String(r.owner_mobile || r.mobile || "").replace(/\D/g, "");
  const num = ph ? `91${ph.slice(-10)}` : "";
  window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
};

const docChaseWaAll = (list) => {
  const msg = [
    "⚠ *NTC — Vehicle Document Renewals Due*",
    `${list.length} vehicle(s) with RC / insurance / PUC / permit expired or expiring this month:`,
    "",
    ...list.map((r) => `• ${r.vehicle_no || "—"} (${r.owner_name || "owner?"}, ${r.owner_mobile || r.mobile || "no mobile"}) — ${docsDue(r).map((d) => `${d.label} ${d.expired ? "EXPIRED" : "expires"} ${fmtDate(d.date)}`).join(", ")}`),
    "",
    "Chase the owners for renewals.",
  ].join("\n");
  const digits = String(localStorage.getItem("ntc_company_mobile") || "").replace(/\D/g, "");
  const num = digits ? (digits.length === 10 ? "91" + digits : digits) : "";
  window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
};

const exportDriversCsv = (list) => {
  const headers = ["Sl No.", "DM No.", "Driver Name", "Branch", "Driver Mobile No.", "License No.", "DL Expiry Date", "Driver Address", "Vehicle No.", "Vehicle Type", "Vehicle Owner Name", "Vehicle Owner Mobile No.", "Vehicle Owner Address", "Vehicle Owner Aadhar", "RC Expiry", "Insurance Expiry", "PUC Expiry", "Permit Expiry", "Documents Attached", "Remarks"];
  const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const lines = [headers.join(",")];
  list.forEach((r, i) => lines.push([i + 1, r.dm_no || "", r.driver_name || "", r.branch || "", r.mobile || "", r.license_no || "", r.dl_expiry || "", r.address || "", r.vehicle_no || "", r.vehicle_type || "", r.owner_name || "", r.owner_mobile || "", r.owner_address || "", r.owner_aadhar || "", r.rc_expiry || "", r.insurance_expiry || "", r.puc_expiry || "", r.permit_expiry || "", `${Object.keys(r.documents || {}).length}/8`, r.remarks || ""].map(cell).join(",")));
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ntc-driver-master-${today()}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const DOC_TYPES = [
  { key: "driver_photo", label: "Driver Photo" },
  { key: "driving_license", label: "Driving License" },
  { key: "aadhar_card", label: "Aadhar Card" },
  { key: "rc", label: "RC (Registration Certificate)" },
  { key: "insurance", label: "Insurance" },
  { key: "puc", label: "PUC Certificate" },
  { key: "permit", label: "Permit" },
  { key: "vehicle_photo", label: "Vehicle Photo" },
];

const EMPTY = {
  driver_name: "",
  branch: "",
  mobile: "",
  license_no: "",
  dl_expiry: "",
  address: "",
  vehicle_no: "",
  vehicle_type: "",
  owner_name: "",
  owner_mobile: "",
  owner_address: "",
  owner_aadhar: "",
  insurance_expiry: "",
  puc_expiry: "",
  permit_expiry: "",
  rc_expiry: "",
  fitness_expiry: "",
  remarks: "",
  documents: {},
};

export default function DriverMaster() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [pendingDocs, setPendingDocs] = useState({});
  const [q, setQ] = useState("");
  const [chaseOpen, setChaseOpen] = useState(false);
  const [docChaseOpen, setDocChaseOpen] = useState(false);
  const [challans, setChallans] = useState([]);
  useEffect(() => {
    api.get("/hire-challans").then((r) => setChallans(r.data || [])).catch(() => {});
  }, []);
  const tripCount = (r) => {
    const n10 = (s) => String(s || "").replace(/\D/g, "").slice(-10);
    const nm = (s) => String(s || "").trim().toLowerCase();
    return challans.filter((c) =>
      (r.mobile && n10(c.driver_phone) && n10(c.driver_phone) === n10(r.mobile)) ||
      (r.license_no && nm(c.dl_no) && nm(c.dl_no) === nm(r.license_no)) ||
      (r.driver_name && nm(c.driver_name) === nm(r.driver_name))
    ).length;
  };
  const [tripsFor, setTripsFor] = useState(null);
  const [tripRows, setTripRows] = useState(null);
  useEffect(() => {
    if (!tripsFor) return;
    setTripRows(null);
    api.get("/hire-challans").then((r) => {
      const n10 = (s) => String(s || "").replace(/\D/g, "").slice(-10);
      const nm = (s) => String(s || "").trim().toLowerCase();
      const trips = (r.data || []).filter((c) =>
        (tripsFor.mobile && n10(c.driver_phone) && n10(c.driver_phone) === n10(tripsFor.mobile)) ||
        (tripsFor.license_no && nm(c.dl_no) && nm(c.dl_no) === nm(tripsFor.license_no)) ||
        (tripsFor.driver_name && nm(c.driver_name) === nm(tripsFor.driver_name))
      ).sort((a, b) => String(b.loading_date || b.date || "").localeCompare(String(a.loading_date || a.date || "")));
      setTripRows(trips);
    }).catch(() => setTripRows([]));
  }, [tripsFor]);
  const fileInputs = useRef({});
  const token = localStorage.getItem("ntc_token");

  const load = async () => {
    try {
      const res = await api.get("/driver-masters");
      setRows(res.data);
      return res.data;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return [];
    }
  };
  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!open) return;
    const veh = String(form.vehicle_no || "").trim();
    if (!veh || form.rc_expiry) return;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/vehicle-docs/${encodeURIComponent(veh)}`);
        const rc = (r.data || []).find((d) => d.doc_type === "RC" && d.valid_upto);
        if (rc) {
          setForm((f) => (f.rc_expiry ? f : { ...f, rc_expiry: String(rc.valid_upto).slice(0, 10) }));
          toast.info(`RC Expiry Date filled from the vehicle vault of ${veh}`);
        }
      } catch {
        /* vault lookup is best-effort */
      }
    }, 600);
    return () => clearTimeout(t);
  }, [open, form.vehicle_no]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setCurrent(null);
    setForm({ ...EMPTY, branch: BRANCHES.includes(user?.office) ? user.office : "" });
    setPendingDocs({});
    setOpen(true);
  };
  const openEdit = (row) => {
    setCurrent(row);
    setForm({ ...EMPTY, ...row });
    setPendingDocs({});
    setOpen(true);
  };

  const save = async () => {
    if (!form.driver_name) {
      toast.error("Driver name is required");
      return;
    }
    setSaving(true);
    try {
      let saved;
      if (current?.id) {
        const res = await api.put(`/driver-masters/${current.id}`, form);
        saved = res.data;
        toast.success("Driver master updated");
      } else {
        const res = await api.post("/driver-masters", form);
        saved = res.data;
        toast.success("Driver master created — you can now attach documents below");
        const staged = Object.entries(pendingDocs).filter(([, f]) => f);
        for (const [k, f] of staged) {
          const fd = new FormData();
          fd.append("file", f);
          try {
            await api.post(`/driver-masters/${saved.id}/document/${k}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          } catch {
            toast.error(`Could not upload ${k === "rc" ? "RC" : "DL"} — attach it from the documents list below`);
          }
        }
        if (staged.length) {
          setPendingDocs({});
          toast.success(`${staged.length} document(s) attached`);
        }
      }
      const all = await load();
      const fresh = all.find((r) => r.id === saved.id) || saved;
      setCurrent(fresh);
      setForm({ ...EMPTY, ...fresh });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete driver master for ${row.driver_name}?`)) return;
    try {
      await api.delete(`/driver-masters/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const pick = (docKey) => fileInputs.current[docKey]?.click();

  const onFile = async (docKey, e) => {
    const file = e.target.files?.[0];
    if (!file || !current?.id) return;
    setUploading(docKey);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/driver-masters/${current.id}/document/${docKey}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      const all = await load();
      const updated = all.find((r) => r.id === current.id);
      if (updated) {
        setCurrent(updated);
        setForm((f) => ({ ...f, documents: updated.documents || {} }));
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading("");
      if (fileInputs.current[docKey]) fileInputs.current[docKey].value = "";
    }
  };

  const removeDoc = async (docKey) => {
    if (!current?.id) return;
    if (!window.confirm("Remove this attached copy?")) return;
    try {
      await api.delete(`/driver-masters/${current.id}/document/${docKey}`);
      const docs = { ...(form.documents || current?.documents || {}) };
      delete docs[docKey];
      setCurrent((c) => (c ? { ...c, documents: docs } : c));
      setForm((f) => ({ ...f, documents: docs }));
      toast.success("Attachment removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const docUrl = (doc) => `${API}/files/${doc.path}?auth=${token}`;
  const docCount = (r) => Object.keys(r.documents || {}).length;
  const [chipFilter, setChipFilter] = useState(null);
  const [branchFilter, setBranchFilter] = useState("All Branches");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [dmMin, setDmMin] = useState(false);
  const [dmMax, setDmMax] = useState(false);
  const [selIds, setSelIds] = useState([]);
  const shown = (rows || []).filter((r) => {
    if (!isAdmin && r.branch && (r.branch || "") !== (user?.office || "")) return false;
    if (isAdmin && branchFilter !== "All Branches" && (r.branch || "") !== branchFilter) return false;
    if (chipFilter && docHealth(r).level !== chipFilter) return false;
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    return [r.dm_no, r.driver_name, r.mobile, r.license_no, r.vehicle_no, r.owner_name]
      .some((x) => String(x || "").toLowerCase().includes(needle));
  });

  return (
    <div>
      <PageHeader
        title="Driver Master"
        subtitle="Maintain driver & vehicle records with document uploads — Driving License, RC, Insurance, PUC, Permit and Vehicle Photo."
        action={
          <div className="flex flex-wrap items-center gap-2">
            {(rows || []).filter(dlExpired).length ? (
              <span className="flex items-center gap-1.5 rounded-md bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700" title="Drivers whose Driving Licence has expired — renew before despatch" data-testid="dl-expired-alert">
                <AlertTriangle className="h-4 w-4" /> {(rows || []).filter(dlExpired).length} DL expired
              </span>
            ) : null}
            <div className="relative">
              <Input
                className="h-9 w-[220px] pr-7"
                placeholder="Search driver / mobile / vehicle"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="driver-master-search"
              />
              {q && (
                <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setQ("")} data-testid="driver-master-search-clear">✕</button>
              )}
            </div>
            {isAdmin ? (
              <>
                <select
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  value={branchFilter}
                  onChange={(e) => setBranchFilter(e.target.value)}
                  title="Show only one branch's drivers"
                  data-testid="driver-master-branch-filter"
                >
                  {["All Branches", ...BRANCHES].map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
                {BRANCHES.map((b) => {
                  const n = (rows || []).filter((r) => (r.branch || "") === b).length;
                  const active = branchFilter === b;
                  return n ? (
                    <button
                      type="button"
                      key={b}
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                      title={active ? "Tap to show all branches again" : `Tap to show only ${b}'s drivers`}
                      onClick={() => setBranchFilter(active ? "All Branches" : b)}
                      data-testid={`dm-branch-count-${b.toLowerCase()}`}
                    >
                      {b} <b className={active ? "" : "text-primary"}>{n}</b>
                    </button>
                  ) : null;
                })}
              </>
            ) : (
              <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700" data-testid="dm-branch-lock-pill">
                Showing your branch: {user?.office || "—"}
              </span>
            )}
            <Button variant="outline" onClick={() => setChaseOpen(true)} data-testid="dl-chase-btn">
              <MessageCircle className="mr-2 h-4 w-4" /> DL Chase{(rows || []).filter(dlDue).length ? ` (${(rows || []).filter(dlDue).length})` : ""}
            </Button>
            <Button variant="outline" onClick={() => setDocChaseOpen(true)} data-testid="doc-chase-btn">
              <MessageCircle className="mr-2 h-4 w-4" /> Doc Chase{(rows || []).filter((r) => docsDue(r).length).length ? ` (${(rows || []).filter((r) => docsDue(r).length).length})` : ""}
            </Button>
            {(() => {
              const red = (rows || []).filter((r) => docHealth(r).level === "red").length;
              const amber = (rows || []).filter((r) => docHealth(r).level === "amber").length;
              if (!red && !amber) return null;
              return (
                <span className="inline-flex items-center gap-1.5">
                  {red > 0 && (
                    <button
                      type="button"
                      onClick={() => setChipFilter(chipFilter === "red" ? null : "red")}
                      className={`rounded-full px-2.5 py-1 text-xs font-bold transition-colors ${chipFilter === "red" ? "bg-red-600 text-white" : "bg-red-100 text-red-700 hover:bg-red-200"}`}
                      title="Tap to show only drivers with expired documents"
                      data-testid="chip-filter-expired"
                    >
                      {red} expired
                    </button>
                  )}
                  {amber > 0 && (
                    <button
                      type="button"
                      onClick={() => setChipFilter(chipFilter === "amber" ? null : "amber")}
                      className={`rounded-full px-2.5 py-1 text-xs font-bold transition-colors ${chipFilter === "amber" ? "bg-amber-500 text-white" : "bg-amber-100 text-amber-800 hover:bg-amber-200"}`}
                      title="Tap to show only drivers with documents due this month"
                      data-testid="chip-filter-due"
                    >
                      {amber} due
                    </button>
                  )}
                  {chipFilter && (
                    <button type="button" onClick={() => setChipFilter(null)} className="text-xs text-muted-foreground underline" data-testid="chip-filter-clear">
                      clear
                    </button>
                  )}
                </span>
              );
            })()}
            <Button variant="outline" disabled={!shown.length} onClick={() => exportDriversCsv(selIds.length ? shown.filter((r) => selIds.includes(r.id)) : shown)} data-testid="export-driver-master-btn">
              <FileCheck2 className="mr-2 h-4 w-4" /> Excel{selIds.length ? ` (${selIds.length})` : ""}
            </Button>
            <Button variant="outline" disabled={!shown.length} onClick={() => printDriverMaster(selIds.length ? shown.filter((r) => selIds.includes(r.id)) : shown)} data-testid="print-driver-master-btn">
              <Printer className="mr-2 h-4 w-4" /> Print{selIds.length ? ` (${selIds.length})` : ""}
            </Button>
            <Button data-testid="add-driver-master-btn" onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" /> New Driver
            </Button>
          </div>
        }
      />

      <TableShell>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="w-10">
                <Checkbox
                  checked={shown.length > 0 && selIds.length === shown.length}
                  onCheckedChange={(c) => setSelIds(c ? shown.map((r) => r.id) : [])}
                  data-testid="dm-select-all"
                />
              </TableHead>
              {["SL No.", "DM No.", "Driver Name", "Branch", "Driver Mobile No.", "License No.", "DL Expiry Date", "Vehicle No.", "Vehicle Owner", "RC Expiry", "Driver Photo", "DL", "Aadhar", "RC", "Documents"].map((h) => (
                <TableHead key={h} className="text-[11px] uppercase tracking-[0.15em] text-slate-500">{h}</TableHead>
              ))}
              <TableHead className="w-[90px] text-right text-[11px] uppercase tracking-[0.15em] text-slate-500">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null &&
              [1, 2, 3].map((i) => (
                <TableRow key={i}>
                  {Array.from({ length: 15 }).map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-16" /></TableCell>
                  ))}
                </TableRow>
              ))}
            {rows && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={17} className="py-12 text-center text-muted-foreground">
                  No drivers yet. Click “New Driver” to add one and upload documents.
                </TableCell>
              </TableRow>
            )}
            {rows && rows.length > 0 && shown.length === 0 && (
              <TableRow>
                <TableCell colSpan={17} className="py-12 text-center text-muted-foreground">No drivers match "{q}".</TableCell>
              </TableRow>
            )}
            {rows &&
              shown.map((r, slIdx) => (
                <TableRow key={r.id} data-testid="driver-master-row">
                  <TableCell className="w-10">
                    <Checkbox
                      checked={selIds.includes(r.id)}
                      onCheckedChange={(c) => setSelIds((p) => (c ? [...p, r.id] : p.filter((x) => x !== r.id)))}
                      data-testid="dm-select-row"
                    />
                  </TableCell>
                  <TableCell className="tabular-nums">{slIdx + 1}</TableCell>
                  <TableCell className="font-medium">{r.dm_no}</TableCell>
                  <TableCell className="text-sm font-medium">
                    <span className="inline-flex items-center gap-1.5">
                      {r.driver_name}
                      {(() => {
                        const h = docHealth(r);
                        if (h.level === "none") return null;
                        const cls = h.level === "red" ? "bg-red-100 text-red-700" : h.level === "amber" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-700";
                        const txt = h.level === "red" ? `${h.items.length} expired` : h.level === "amber" ? `${h.items.length} due` : "Docs OK";
                        const title = h.items.length ? h.items.map(([l, d]) => `${l}: ${fmtDate(d)}`).join(", ") : "All tracked documents valid";
                        return (
                          <span className={`inline-flex rounded-full px-1.5 py-0.5 text-[9px] font-bold ${cls}`} title={title} data-testid="doc-health-chip">
                            {txt}
                          </span>
                        );
                      })()}
                      {tripCount(r) ? (
                        <button
                          type="button"
                          className="rounded-full bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold text-sky-700 hover:bg-sky-200"
                          title={`${tripCount(r)} challan trip(s) — tap for trip history`}
                          onClick={() => setTripsFor(r)}
                          data-testid="driver-trip-count-badge"
                        >
                          {tripCount(r)} trip{tripCount(r) === 1 ? "" : "s"}
                        </button>
                      ) : null}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm" data-testid="dm-branch-cell">{r.branch || "—"}</TableCell>
                  <TableCell className="text-sm">{r.mobile || "—"}</TableCell>
                  <TableCell className="text-sm">{r.license_no || "—"}</TableCell>
                  <TableCell className="text-sm">
                    {r.dl_expiry ? (
                      <span className={dlExpired(r) ? "font-bold text-red-600" : ""} data-testid={dlExpired(r) ? "dm-dl-expired" : undefined}>
                        {fmtDate(String(r.dl_expiry).slice(0, 10))}
                        {dlExpired(r) ? (
                          <span className="ml-1.5 rounded-full bg-red-600 px-1.5 py-0.5 text-[9px] font-bold text-white">EXPIRED</span>
                        ) : null}
                      </span>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{r.vehicle_no || "—"}</TableCell>
                  <TableCell className="text-sm">{r.owner_name || "—"}</TableCell>
                  <TableCell className="text-sm">
                    {r.rc_expiry ? (
                      <span className={String(r.rc_expiry).slice(0, 10) < today() ? "font-bold text-red-600" : ""} data-testid="dm-rc-expiry-cell">
                        {fmtDate(String(r.rc_expiry).slice(0, 10))}
                        {String(r.rc_expiry).slice(0, 10) < today() ? (
                          <span className="ml-1.5 rounded-full bg-red-600 px-1.5 py-0.5 text-[9px] font-bold text-white">EXPIRED</span>
                        ) : null}
                      </span>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  {["driver_photo", "driving_license", "aadhar_card", "rc"].map((dk) => {
                    const doc = (r.documents || {})[dk];
                    return (
                      <TableCell key={dk}>
                        {doc ? (
                          <a href={docUrl(doc)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700 hover:bg-emerald-100" data-testid={`dm-col-${dk}`}>
                            <ExternalLink className="h-3 w-3" /> View
                          </a>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell>
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${docCount(r) ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-500"}`}>
                      <FileCheck2 className="h-3.5 w-3.5" /> {docCount(r)}/{DOC_TYPES.length}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Trip history — challans run by this driver" onClick={() => setTripsFor(r)} data-testid="driver-trips-btn">
                        <History className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit / upload documents" onClick={() => openEdit(r)} data-testid="edit-driver-master-btn">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => remove(r)} data-testid="delete-driver-master-btn">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableShell>

      <Dialog
        open={open && !dmMin}
        onOpenChange={(o) => {
          if (!o && dmMin) return;
          setOpen(o);
          if (!o) {
            setDmMin(false);
            setDmMax(false);
          }
        }}
      >
        <DialogContent className={dmMax ? "h-[96vh] max-h-[96vh] overflow-y-auto sm:max-w-[96vw]" : "max-h-[92vh] overflow-y-auto sm:max-w-[720px]"}>
          <div className="absolute right-10 top-3 flex gap-1">
            <button type="button" className="rounded p-1 text-muted-foreground hover:bg-slate-100" onClick={() => setDmMin(true)} data-testid="dialog-minimize-btn" title="Minimize">
              <Minimize2 className="h-4 w-4" />
            </button>
            <button type="button" className="rounded p-1 text-muted-foreground hover:bg-slate-100" onClick={() => setDmMax((m) => !m)} data-testid="dialog-maximize-btn" title={dmMax ? "Restore size" : "Maximize"}>
              <Maximize2 className="h-4 w-4" />
            </button>
          </div>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <IdCard className="h-4 w-4 text-primary" /> {current?.id ? "Edit Driver Master" : "New Driver Master"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-2">
            <section>
              <h4 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-primary">Driver Details</h4>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="grid gap-1.5">
                  <Label>Driver Name</Label>
                  <Input data-testid="dm-driver-name" value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} placeholder="Suresh Kumar" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Driver Mobile No.</Label>
                  <Input data-testid="dm-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="9876500000" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Driving License No.</Label>
                  <Input value={form.license_no} onChange={(e) => setForm({ ...form, license_no: e.target.value })} placeholder="TN0120110012345" />
                </div>
                <div className="grid gap-1.5">
                  <Label>DL Expiry Date</Label>
                  <Input type="date" value={form.dl_expiry} onChange={(e) => setForm({ ...form, dl_expiry: e.target.value })} />
                </div>
                <div className="grid gap-1.5">
                  <Label>Branch</Label>
                  <select
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    value={form.branch || ""}
                    onChange={(e) => setForm({ ...form, branch: e.target.value })}
                    data-testid="dm-branch"
                  >
                    <option value="">Select branch</option>
                    {BRANCHES.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div className="grid gap-1.5 sm:col-span-2">
                  <Label>Driver Address</Label>
                  <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="City, State" />
                </div>
              </div>
            </section>

            <section>
              <h4 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-primary">Vehicle Details</h4>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="grid gap-1.5">
                  <Label>Vehicle No.</Label>
                  <Input value={form.vehicle_no} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })} placeholder="TN 01 AB 1234" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Vehicle Type</Label>
                  <Input value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })} placeholder="32 Feet SXL" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Vehicle Owner Name</Label>
                  <Input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} placeholder="Owner / Broker" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Vehicle Owner Mobile No.</Label>
                  <Input data-testid="dm-owner-mobile" value={form.owner_mobile} onChange={(e) => setForm({ ...form, owner_mobile: e.target.value })} placeholder="9876500000" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Vehicle Owner Adhar Card</Label>
                  <Input data-testid="dm-owner-aadhar" value={form.owner_aadhar} onChange={(e) => setForm({ ...form, owner_aadhar: e.target.value })} placeholder="XXXX XXXX XXXX" />
                </div>
                <div className="grid gap-1.5 sm:col-span-3">
                  <Label>Vehicle Owner Address</Label>
                  <Input data-testid="dm-owner-address" value={form.owner_address} onChange={(e) => setForm({ ...form, owner_address: e.target.value })} placeholder="City, State" />
                </div>
                <div className="grid gap-1.5">
                  <Label>RC Expiry Date</Label>
                  <Input type="date" value={form.rc_expiry} onChange={(e) => setForm({ ...form, rc_expiry: e.target.value })} data-testid="dm-rc-expiry-input" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Insurance Expiry</Label>
                  <Input type="date" value={form.insurance_expiry} onChange={(e) => setForm({ ...form, insurance_expiry: e.target.value })} />
                </div>
                <div className="grid gap-1.5">
                  <Label>PUC Expiry</Label>
                  <Input type="date" value={form.puc_expiry} onChange={(e) => setForm({ ...form, puc_expiry: e.target.value })} />
                </div>
                <div className="grid gap-1.5">
                  <Label>Permit Expiry</Label>
                  <Input type="date" value={form.permit_expiry} onChange={(e) => setForm({ ...form, permit_expiry: e.target.value })} />
                </div>
              </div>
              <div className="mt-4 grid gap-1.5">
                <Label>Remarks</Label>
                <Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} placeholder="Any notes" />
              </div>
            </section>

            <section>
              <h4 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-primary">Documents</h4>
              {!current?.id ? (
                <div className="space-y-2" data-testid="dm-docs-locked">
                  <p className="text-xs text-muted-foreground">Attach the DL &amp; RC copies now — they upload automatically when you save. Other documents can be added after saving.</p>
                  {[{ key: "driving_license", label: "Driving License (DL)" }, { key: "rc", label: "RC (Registration Certificate)" }].map((d) => (
                    <div key={d.key} className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
                      <div className="flex min-w-0 items-center gap-2">
                        {pendingDocs[d.key] && /^image\//.test(pendingDocs[d.key].type || "") ? (
                          <img src={URL.createObjectURL(pendingDocs[d.key])} alt={d.label} className="h-10 w-10 shrink-0 rounded border object-cover" data-testid={`dm-new-thumb-${d.key}`} />
                        ) : null}
                        <div className="min-w-0">
                          <div className="text-sm font-medium">{d.label}</div>
                          <div className="truncate text-xs text-muted-foreground">{pendingDocs[d.key] ? pendingDocs[d.key].name : "Not attached"}</div>
                        </div>
                      </div>
                      <label className="shrink-0">
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp,.pdf"
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) setPendingDocs((p) => ({ ...p, [d.key]: f }));
                            e.target.value = "";
                          }}
                          data-testid={`dm-new-file-${d.key}`}
                        />
                        <span className={`inline-flex h-8 cursor-pointer items-center gap-1 rounded-md border px-3 text-xs font-medium ${pendingDocs[d.key] ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "border-border bg-card hover:bg-accent"}`} data-testid={`dm-new-attach-${d.key}`}>
                          <Upload className="h-3.5 w-3.5" /> {pendingDocs[d.key] ? "Change" : "Attach"}
                        </span>
                      </label>
                      {pendingDocs[d.key] ? (
                        <span className="flex shrink-0 items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 gap-1 text-xs"
                            onClick={() => window.open(URL.createObjectURL(pendingDocs[d.key]), "_blank")}
                            data-testid={`dm-new-view-${d.key}`}
                          >
                            <ExternalLink className="h-3.5 w-3.5" /> View
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                            title="Remove this file"
                            onClick={() => setPendingDocs((p) => { const n = { ...p }; delete n[d.key]; return n; })}
                            data-testid={`dm-new-remove-${d.key}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </span>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {DOC_TYPES.map((d) => {
                    const doc = (form.documents || {})[d.key];
                    return (
                      <div key={d.key} className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
                        <div className="flex min-w-0 items-center gap-2">
                          {doc && /\.(jpe?g|png|webp)$/i.test(doc.filename || "") ? (
                            <img
                              src={docUrl(doc)}
                              alt={d.label}
                              loading="lazy"
                              className="h-10 w-10 shrink-0 cursor-pointer rounded border object-cover"
                              onClick={() => window.open(docUrl(doc), "_blank")}
                              data-testid={`dm-thumb-${d.key}`}
                            />
                          ) : null}
                          <div className="min-w-0">
                            <div className="text-sm font-medium">{d.label}</div>
                            <div className="truncate text-xs text-muted-foreground">
                              {doc ? doc.filename : "Not uploaded"}
                            </div>
                          </div>
                        </div>
                        <input
                          ref={(el) => (fileInputs.current[d.key] = el)}
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp,.pdf"
                          className="hidden"
                          onChange={(e) => onFile(d.key, e)}
                          data-testid={`dm-file-input-${d.key}`}
                        />
                        <div className="flex shrink-0 gap-1">
                          {doc && (
                            <a href={docUrl(doc)} target="_blank" rel="noreferrer">
                              <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs" data-testid={`dm-view-${d.key}`}>
                                <ExternalLink className="h-3.5 w-3.5" /> View
                              </Button>
                            </a>
                          )}
                          {doc && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                              title="Remove this copy"
                              disabled={uploading === d.key}
                              onClick={() => removeDoc(d.key)}
                              data-testid={`dm-remove-${d.key}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant={doc ? "outline" : "default"}
                            className="h-8 gap-1 text-xs"
                            disabled={uploading === d.key}
                            onClick={() => pick(d.key)}
                            data-testid={`dm-upload-${d.key}`}
                          >
                            <Upload className="h-3.5 w-3.5" />
                            {uploading === d.key ? "Uploading…" : doc ? "Replace" : "Upload"}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
            <Button onClick={save} disabled={saving} data-testid="save-driver-master-btn">
              {saving ? "Saving…" : current?.id ? "Save Changes" : "Save & Add Documents"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {open && dmMin && (
        <button
          type="button"
          onClick={() => setDmMin(false)}
          className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-full border border-border bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-lg"
          data-testid="dialog-restore-btn"
        >
          <Maximize2 className="h-3.5 w-3.5" /> {current?.id ? "Edit Driver Master" : "New Driver Master"} (draft) — Restore
        </button>
      )}

      <Dialog open={chaseOpen} onOpenChange={setChaseOpen}>
        <DialogContent className="sm:max-w-[720px]" data-testid="dl-chase-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">DL Chase — Expired / Expiring This Month</DialogTitle>
          </DialogHeader>
          {(() => {
            const due = (rows || []).filter(dlDue);
            return (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground" data-testid="dl-chase-summary">
                    {due.length} driver(s) with a licence expired or expiring this month — WhatsApp each one a renewal reminder.
                  </p>
                  <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" disabled={!due.length} onClick={() => dlChaseWaAll(due)} data-testid="dl-chase-wa-all-btn">
                    <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> Full List → My WhatsApp
                  </Button>
                </div>
                <div className="max-h-[360px] overflow-auto rounded border">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-muted">
                      <tr className="text-left">
                        <th className="p-2">Driver</th>
                        <th className="p-2">Mobile</th>
                        <th className="p-2">License No.</th>
                        <th className="p-2">DL Expiry</th>
                        <th className="p-2">Vehicle</th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {due.map((r) => {
                        const exp = String(r.dl_expiry).slice(0, 10) < today();
                        return (
                          <tr key={r.id} className="border-t" data-testid="dl-chase-row">
                            <td className="p-2 font-semibold">{r.driver_name}</td>
                            <td className="whitespace-nowrap p-2">{r.mobile || "—"}</td>
                            <td className="p-2">{r.license_no || "—"}</td>
                            <td className="whitespace-nowrap p-2">
                              <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${exp ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>
                                {fmtDate(String(r.dl_expiry).slice(0, 10))} · {exp ? "EXPIRED" : "this month"}
                              </span>
                            </td>
                            <td className="p-2">{r.vehicle_no || "—"}</td>
                            <td className="p-2 text-right">
                              <Button size="sm" className="h-7 bg-emerald-600 px-2.5 text-xs text-white hover:bg-emerald-700" onClick={() => dlChaseWa(r)} data-testid="dl-chase-wa-btn">
                                <MessageCircle className="mr-1 h-3.5 w-3.5" /> WhatsApp
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                      {due.length === 0 && (
                        <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">No licences expired or expiring this month.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={docChaseOpen} onOpenChange={setDocChaseOpen}>
        <DialogContent className="sm:max-w-[760px]" data-testid="doc-chase-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Vehicle Doc Chase — RC / Insurance / PUC / Permit lapsing this month</DialogTitle>
          </DialogHeader>
          {(() => {
            const due = (rows || []).filter((r) => docsDue(r).length);
            return (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground" data-testid="doc-chase-summary">
                    {due.length} vehicle(s) with documents expired or expiring this month — WhatsApp each owner a renewal reminder.
                  </p>
                  <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" disabled={!due.length} onClick={() => docChaseWaAll(due)} data-testid="doc-chase-wa-all-btn">
                    <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> Full List → My WhatsApp
                  </Button>
                </div>
                <div className="max-h-[360px] overflow-auto rounded border">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-muted">
                      <tr className="text-left">
                        <th className="p-2">Vehicle</th>
                        <th className="p-2">Owner</th>
                        <th className="p-2">Owner Mobile</th>
                        <th className="p-2">Documents Due</th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {due.map((r) => (
                        <tr key={r.id} className="border-t" data-testid="doc-chase-row">
                          <td className="p-2 font-semibold">{r.vehicle_no || "—"}</td>
                          <td className="p-2">{r.owner_name || "—"}</td>
                          <td className="whitespace-nowrap p-2">{r.owner_mobile || r.mobile || "—"}</td>
                          <td className="p-2">
                            <span className="inline-flex flex-wrap gap-1">
                              {docsDue(r).map((d) => (
                                <span key={d.label} className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${d.expired ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>
                                  {d.label} · {fmtDate(d.date)} · {d.expired ? "EXPIRED" : "this month"}
                                </span>
                              ))}
                            </span>
                          </td>
                          <td className="p-2 text-right">
                            <Button size="sm" className="h-7 bg-emerald-600 px-2.5 text-xs text-white hover:bg-emerald-700" onClick={() => docChaseWa(r)} data-testid="doc-chase-wa-btn">
                              <MessageCircle className="mr-1 h-3.5 w-3.5" /> WhatsApp Owner
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {due.length === 0 && (
                        <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">No RC, insurance, PUC or permit expired or expiring this month.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={!!tripsFor} onOpenChange={(o) => { if (!o) setTripsFor(null); }}>
        <DialogContent className="sm:max-w-[760px]" data-testid="driver-trips-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Trip History — {tripsFor?.driver_name || ""}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground" data-testid="driver-trips-summary">
              {tripRows === null ? "Loading challans…" : `${tripRows.length} challan(s) run by ${tripsFor?.driver_name || "this driver"} (matched by mobile, licence or name).`}
            </p>
            <Button variant="outline" size="sm" disabled={!tripRows?.length} onClick={() => printDriverTrips(tripsFor, tripRows)} data-testid="driver-trips-print-btn">
              <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
            </Button>
          </div>
          <div className="max-h-[380px] overflow-auto rounded border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted">
                <tr className="text-left">
                  <th className="p-2">Challan No.</th>
                  <th className="p-2">Loading Date</th>
                  <th className="p-2">Vehicle</th>
                  <th className="p-2">Route</th>
                  <th className="p-2 text-right">Vehicle Hire</th>
                  <th className="p-2 text-right">Balance</th>
                  <th className="p-2">POD</th>
                </tr>
              </thead>
              <tbody>
                {(tripRows || []).map((c) => (
                  <tr key={c.id} className="border-t" data-testid="driver-trip-row">
                    <td className="p-2 font-bold">{String(c.challan_no || "").replace(/^VHC[-\s]?/i, "") || "—"}</td>
                    <td className="whitespace-nowrap p-2">{fmtDate(String(c.loading_date || c.date || "").slice(0, 10))}</td>
                    <td className="p-2">{c.vehicle_no || "—"}</td>
                    <td className="p-2">{[c.origin, c.destination].filter(Boolean).join(" → ") || "—"}</td>
                    <td className="p-2 text-right">₹{Number(c.hire_amount || 0).toLocaleString("en-IN")}</td>
                    <td className="p-2 text-right">₹{Number(c.balance || 0).toLocaleString("en-IN")}</td>
                    <td className="p-2">
                      {c.pod_ack_received ? (
                        <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">✓ Back</span>
                      ) : (
                        <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">Out</span>
                      )}
                    </td>
                  </tr>
                ))}
                {tripRows !== null && tripRows.length === 0 && (
                  <tr><td colSpan={7} className="p-4 text-center text-muted-foreground">No challans found for this driver yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
